import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalBody,
  ModalHeader,
} from "reactstrap";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import Login from "./Login/Login";
import GenreCarousel from "./Genre";
import HowItWorks from "./HowItWorks";
import Footer from "./Footer";
import PricingSection from "./PricingSection";
import HomePage from "./Home/HomeTopSection";
import MyNavbar from "./MyNavbar";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const Home = ({ setUserRights }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const navigate = useNavigate();
  const userData = JSON.parse(sessionStorage.getItem("userData")) || {};

  const toggleModal = () => setModalOpen((p) => !p);

  useEffect(() => {
    setShowNavbar(true);

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      setLastScrollY((prevScrollY) => {
        if (currentScrollY <= 20) {
          setShowNavbar(true);
        } else {
          setShowNavbar(currentScrollY < prevScrollY);
        }
        return currentScrollY;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="modern-master-wrapper" style={{ overflowX: 'hidden' }}>
      {/* Scope-contained style override matching global premium styling presets */}
      <style>{`
        :root {
          --brand-primary: #6366f1;
          --slate-900: #0f172a;
          --slate-600: #475569;
          --slate-100: #f1f5f9;
          --radius-card: 16px;
          --shadow-premium: 0 10px 30px rgba(15, 23, 42, 0.04);
          --transition-ease: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .modern-master-wrapper {
          background-color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        /* Feature Section Styling */
        .section-tagline {
          color: var(--brand-primary);
          font-weight: 600;
          font-size: 0.9rem;
          text-transform: uppercase;
          letter-spacing: 1px;
          display: block;
          margin-bottom: 8px;
        }

        .section-main-title {
          font-weight: 800;
          color: var(--slate-900);
          letter-spacing: -1px;
        }

        .premium-feature-card {
          border: 1px solid rgba(0, 0, 0, 0.05) !important;
          border-radius: var(--radius-card) !important;
          box-shadow: var(--shadow-premium) !important;
          transition: var(--transition-ease) !important;
          background: #ffffff;
        }
        .premium-feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(99, 102, 241, 0.08) !important;
          border-color: rgba(99, 102, 241, 0.2) !important;
        }

        .feature-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          font-weight: bold;
        }
        
        .icon-bg-primary { background: rgba(99, 102, 241, 0.1); color: var(--brand-primary); }
        .icon-bg-success { background: rgba(34, 197, 94, 0.1); color: #22c55e; }
        .icon-bg-warning { background: rgba(245, 158, 11, 0.1); color: #f59e0b; }

        .feature-card-title {
          font-weight: 700;
          color: var(--slate-900);
          margin-bottom: 10px;
          font-size: 1.15rem;
        }

        /* Image Showcase Layout adjustments */
        .premium-image-frame {
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.03);
        }

        /* Check row elements */
        .premium-check-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 14px;
          color: var(--slate-600);
          font-weight: 500;
        }

        .premium-check-circle {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(99, 102, 241, 0.1);
          color: var(--brand-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        /* Alternative Light sections background */
        .bg-premium-light {
          background-color: #f8fafc !important;
        }

        /* Premium Modal Overrides */
        .modal-content {
          border-radius: 20px !important;
          border: none !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
        }
        .modal-header {
          border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
          font-weight: 700 !important;
        }
      `}</style>

      <MyNavbar
        userData={userData}
        navigate={navigate}
        toggleModal={toggleModal}
        showNavbar={showNavbar}
      />

      {/* HERO SECTION CONTAINER */}
      <section className="hero-section">
        <HomePage setUserRights={setUserRights} />
      </section>

      {/* WHY READERS LOVE US (FEATURES) */}
      <section id="features" className="py-5 bg-premium-light">
        <Container className="py-4">
          <Row className="text-center mb-5">
            <Col>
              <span className="section-tagline">Core Features</span>
              <h2 className="section-main-title h1">Why Readers Love Us</h2>
              <p className="text-muted max-w-md mx-auto fs-5">
                Everything you need for a smooth, distraction-free reading experience.
              </p>
            </Col>
          </Row>

          <Row className="gy-4 mx-0">
            {[
              {
                title: "Wide Collection",
                desc: "Explore deep catalogs across fiction, non-fiction, and localized academic resources easily.",
                color: "primary",
                iconSVG: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path></svg>
                )
              },
              {
                title: "Flexible Rentals",
                desc: "Rent and interchange books monthly on your own terms with absolute zero long term commitment.",
                color: "success",
                iconSVG: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                )
              },
              {
                title: "Affordable Pricing",
                desc: "Gain uninhibited access to premium hardcover volumes starting at just ₹450/month.",
                color: "warning",
                iconSVG: (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>
                )
              },
            ].map((item, i) => (
              <Col md="4" key={i} className="px-3">
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="h-100"
                >
                  <Card className="h-100 premium-feature-card p-4">
                    <CardBody className="p-0">
                      <div className={`feature-icon-wrapper icon-bg-${item.color}`}>
                        {item.iconSVG}
                      </div>
                      <h3 className="feature-card-title">{item.title}</h3>
                      <p className="text-muted mb-0 lh-base">{item.desc}</p>
                    </CardBody>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* WHY US SHOWCASE */}
      <section id="why-us" className="py-5 bg-white">
        <Container className="py-4">
          <Row className="align-items-center gy-5 mx-0">
            <Col md="6" className="d-flex justify-content-center align-items-center px-3">
              <motion.div
                className="premium-image-frame"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <img 
                  src="/images/img7.jpeg" 
                  alt="Modern Minimalist Reading Workspace" 
                  className="img-fluid d-block" 
                  style={{ width: '100%', height: 'auto', objectFit: 'cover' }} 
                />
              </motion.div>
            </Col>

            <Col md="6" className="px-md-5 px-3">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <span className="section-tagline">Our Philosophy</span>
                <h2 className="section-main-title h1 mb-3">A Better Way to Read</h2>
                <p className="text-muted fs-5 mb-4 lh-base">
                  Library Rentals connects readers with books in the simplest, smartest, and most affordable ecosystem possible.
                </p>

                <div className="mt-2">
                  {[
                    "Thousands of curated global titles across genres",
                    "Hassle-free, skip-or-cancel monthly subscription frameworks",
                    "Dedicated, friendly on-demand digital support experts",
                  ].map((text, i) => (
                    <div key={i} className="premium-check-row">
                      <div className="premium-check-circle">✓</div>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* GENRES DISPLAY CAROUSEL */}
      <section className="py-5 bg-premium-light">
        <Container>
          <GenreCarousel />
        </Container>
      </section>

      {/* HOW IT WORKS PROCESS SECTION */}
      <section id="how-it-works" className="py-5 bg-white">
        <Container>
          <HowItWorks />
        </Container>
      </section>

      {/* PRICING SYSTEM DISPLAY */}
      <section id="pricing" className="py-5 bg-premium-light">
        <Container className="py-4">
          <motion.div
            className="text-center mb-5"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
        <span className="section-tagline">Simple Memberships</span>
<h2 className="section-main-title h1 mb-2">Transparent Reading Plans</h2>
<p className="text-muted fs-5">No setup fees or hidden charges. Change your plan limit or pause anytime.</p>
<h4 className="mt-3 font-weight-bold" style={{ color: 'var(--slate-900)' }}>
  Borrowing starting at just <span style={{ color: 'var(--brand-primary)', fontWeight: '800' }}>₹450 / month</span>
</h4>
          </motion.div>

          <PricingSection />
        </Container>
      </section>

      {/* FOOTER ARCHITECTURE */}
      <Footer />

      {/* MODAL ACCOUNT AUTHENTICATION PORTAL */}
      <Modal isOpen={modalOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal} className="px-4 pt-4">Welcome Back</ModalHeader>
        <ModalBody className="px-4 pb-4">
          <Login setUserRights={setUserRights} />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default Home;